import { MeshTransmissionMaterial, useGLTF } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import type { ReactElement } from 'react';
import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import type { GLTF } from 'three-stdlib';
import { bezier, reMap } from '../../../util/math.utils';
import useWindowSize from '../../../hooks/useWindowSize';
import { isTouch } from '../../../hooks/useIsDesktopDevice';

type GLTFResult = GLTF & {
  nodes: {
    BG: THREE.Mesh;
    Enso_1: THREE.Mesh;
  };
  materials: {
    ['RS Material']: THREE.MeshStandardMaterial;
    ['RS Material.2']: THREE.MeshStandardMaterial;
  };
};

type PropsType = {
  progressRef: React.MutableRefObject<number>;
  backgroundColor?: string;
  shapeColor?: string;
  homepage?: boolean;
  isCurrent?: boolean;
};

export function Enso(props: PropsType): ReactElement {
  const group = useRef<THREE.Group>(null!);
  const { nodes, animations } = useGLTF('/assets/3d/Shapes/Enso.glb') as unknown as GLTFResult;
  const { width } = useWindowSize();

  const mixer = useRef<THREE.AnimationMixer>(undefined);
  const meshGroupRef = useRef<THREE.Group>(null);

  useEffect(() => {
    if (!group.current) return;
    mixer.current = new THREE.AnimationMixer(group.current);
    const action = mixer.current.clipAction(animations[0]);
    action.play();
  }, [animations]);

  useFrame(() => {
    if (!mixer?.current || !props.progressRef || !props.isCurrent) return;
    if (props.progressRef.current < 100) {
      const p = props.progressRef.current;
      const b = bezier(p / 100, 0, 0.5, 0.5, 1);
      const t_bezier = b * animations[0].duration;
      const t = (p * animations[0].duration) / 100;
      mixer.current.setTime(props.homepage ? t_bezier : t);
    }
  });

  const material = props.homepage ? (
    <MeshTransmissionMaterial
      backside
      samples={1}
      roughness={0.1}
      iridescence={1}
      iridescenceIOR={0.65}
      backsideResolution={32}
      iridescenceThicknessRange={[0, 200]}
      // clearcoat={0}
      envMapIntensity={0.7}
      background={
        new THREE.Color(!props.homepage && props.shapeColor ? props.shapeColor : '#4C4744')
      }
      distortion={0}
      distortionScale={0}
      temporalDistortion={0}
      transparent={!props.homepage}
      opacity={props.homepage ? 1 : 0.85}
      clearcoat={1}
    />
  ) : (
    <MeshTransmissionMaterial
      backside
      transparent
      samples={1}
      roughness={isTouch ? 0 : 0.1}
      iridescence={isTouch ? 0 : 0.8}
      iridescenceIOR={isTouch ? 0.1 : 0.65}
      backsideResolution={32}
      iridescenceThicknessRange={[0, 100]}
      envMapIntensity={0.7}
      opacity={props.homepage ? 1 : 0.85}
      // Adding any missing properties from previous components
      transmission={1}
      backsideThickness={0}
      chromaticAberration={0.03}
      transmissionSampler={false}
      clearcoat={1}
      clearcoatRoughness={isTouch ? 0 : 0.1}
      reflectivity={0.5}
      sheenColor="#FFFFFF"
      sheen={0}
      metalness={0}
      ior={1.5}
      specularColor="white"
      specularIntensity={isTouch ? 0.1 : 0.5}
      attenuationColor="white"
      thickness={0}
      anisotropicBlur={isTouch ? 0 : 0.1}
      distortion={0}
      distortionScale={0}
      temporalDistortion={0}
      background={new THREE.Color(props.shapeColor || '#4C4744')}
    />
  );

  return (
    <group ref={group} {...props} dispose={null} visible={props.isCurrent}>
      <group>
        <group ref={meshGroupRef} position={[0, 4, -150]}>
          <group
            name="Enso"
            position={[0, 0, -608.548]}
            rotation={[-0.611, 0, 0]}
            scale={Math.min(0.95, reMap(width, 320, 650, 0.575, 0.95))}
          >
            {props.homepage && (
              <mesh visible={false} geometry={nodes.Enso_1.geometry}>
                {material}
              </mesh>
            )}
            <mesh name="Enso_1" geometry={nodes.Enso_1.geometry}>
              {material}
            </mesh>
          </group>
        </group>
      </group>
    </group>
  );
}

useGLTF.preload('/assets/3d/Shapes/Enso.glb');

export default Enso;
